'use client';

import React from 'react';
import { cn } from '@/lib/cn';
import { IconLocationPin, IconChevronRight } from '@repo/svgs';
import type { RecentBuildingSearch } from '@/utils/recentSearches';
import { deriveBoroughFromAddress } from '@repo/utils';
import useNavigateToSearchedBuilding from '@/features/buildings/hooks/useNavigateToSearchedBuilding';

interface RecentBuildingSearchCardProps {
  search: RecentBuildingSearch;
  className?: string;
}

export function RecentBuildingSearchCard({ search, className }: RecentBuildingSearchCardProps) {
  const navigateToSearchedBuilding = useNavigateToSearchedBuilding();

  // NYC -> listings page with this address as the location; other cities -> building page.
  const handleClick = () => navigateToSearchedBuilding(search);

  const buildingName = search.streetAddress;

  const borough = deriveBoroughFromAddress(search.fullAddress);

  return (
    <div
      className={cn(
        'flex items-center gap-3 w-full shadow p-4 rounded-2xl cursor-pointer',
        'border border-secondary hover:bg-primary-hover bg-primary',
        'transition-colors duration-200',
        className,
      )}
      onClick={handleClick}
    >
      {/* Location Icon */}
      <IconLocationPin className="w-6 h-6 icon-secondary shrink-0" />

      {/* Content Container */}
      <div className="flex flex-col gap-1 min-w-0 flex-1">
        {/* Building Name/Address */}
        <h3 className="text-body-2-high text-primary truncate w-full overflow-ellipsis overflow-hidden whitespace-nowrap">
          {buildingName}
        </h3>

        {/* Borough */}
        <p className="text-caption text-secondary truncate w-full overflow-ellipsis overflow-hidden whitespace-nowrap">
          {borough}
        </p>
      </div>

      {/* Chevron Right */}
      <IconChevronRight className="w-3 h-3 icon-tertiary shrink-0" />
    </div>
  );
}
